import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getDefaultGroupTicketDesign,
  loadGroupDesign,
} from "../services/groupTicketDesignApi";

export default function useGroupTicketDesign(groupId) {
  const cloneDesign = useCallback(
    (value) => (value ? JSON.parse(JSON.stringify(value)) : null),
    [],
  );
  const storageKey = `${groupId || "no-group"}`;
  const [design, setDesignState] = useState(null);
  const [initialDesign, setInitialDesign] = useState(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      let stored = null;

      if (groupId) {
        try {
          stored = await loadGroupDesign(groupId);
        } catch (err) {
          console.error("useGroupTicketDesign: loadGroupDesign failed", err);
        }
      }

      if (!stored) {
        try {
          stored = await getDefaultGroupTicketDesign();
        } catch (err) {
          console.error(
            "useGroupTicketDesign: getDefaultGroupTicketDesign failed",
            err,
          );
        }
      }

      if (!mounted) return;

      let nextDesign = stored ? { ...stored } : null;
      if (nextDesign) {
        const parsedCanvasBorderWidth = Number(nextDesign?.canvas?.borderWidth);
        nextDesign = {
          ...nextDesign,
          canvas: {
            ...(nextDesign.canvas || {}),
            borderWidth:
              Number.isFinite(parsedCanvasBorderWidth) &&
              parsedCanvasBorderWidth > 0
                ? parsedCanvasBorderWidth
                : 1,
          },
        };
      }
      setInitialDesign(cloneDesign(nextDesign));
      setDesignState(nextDesign);
    })();

    return () => {
      mounted = false;
    };
  }, [cloneDesign, groupId, storageKey]);

  const updateDesign = (updaterOrPatch) => {
    setDesignState((prev) => {
      if (typeof updaterOrPatch === "function") {
        return updaterOrPatch(prev);
      }
      if (!prev) return updaterOrPatch;
      const next = { ...prev, ...updaterOrPatch };
      return next;
    });
  };

  const sections = useMemo(() => {
    if (!design) return [];
    return design.sectionsOrder
      .map((id) => ({ id, def: design.sections[id] }))
      .filter((s) => s.def);
  }, [design]);

  const replaceDesign = useCallback((nextDesign) => {
    setDesignState(nextDesign);
  }, []);

  const markSavedDesign = useCallback(
    (nextDesign) => {
      setInitialDesign(cloneDesign(nextDesign));
    },
    [cloneDesign],
  );

  return { design, setDesign: updateDesign, replaceDesign, sections, initialDesign, markSavedDesign };
}
